// ✅ src/pages/VehicleV2/VehicleSubTypeMaster.jsx
// Visual design aligned with ProductionBOMListing.
// VehicleTypeId comes from URL query: ?vehicletypeid=2081
//
// Endpoints used:
//   GET  /api/equipmentTypeMaster/vehicle-types
//   GET  /api/equipmentTypeMaster/list?vehicleTypeId=..
//   POST /api/equipmentTypeMaster/save
//   POST /api/equipmentTypeMaster/delete

import HeaderBar from "../../components/HeaderBar";
import { useEffect, useMemo, useRef, useState, useContext } from "react";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaTrash, FaPlus } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/equipmentTypeMaster";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v) => String(v ?? "").trim();

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};

const VehicleSubTypeMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;
  const navigate = useNavigate();

  const formRef = useRef(null);
  const inputRef = useRef(null);

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branch = getLS("branchType") || getLS("branchtype") || "";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [searchParams] = useSearchParams();
  const urlVehicleTypeId = toInt(searchParams.get("vehicletypeid"), 0);

  const [vehicleTypeId, setVehicleTypeId] = useState(
    urlVehicleTypeId ? String(urlVehicleTypeId) : ""
  );

  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [subTypes, setSubTypes] = useState([]);

  const [formData, setFormData] = useState({
    vehicleSubType: "",
  });

  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fieldClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const vehicleTypeNameById = useMemo(() => {
    const m = new Map();
    vehicleTypes.forEach((v) => m.set(String(v.id), v.name));
    return m;
  }, [vehicleTypes]);

  const selectedVehicleTypeName =
    vehicleTypeNameById.get(String(vehicleTypeId)) || "";

  useEffect(() => {
    if (urlVehicleTypeId) setVehicleTypeId(String(urlVehicleTypeId));
  }, [urlVehicleTypeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const fetchVehicleTypes = async () => {
    try {
      const r = await axios.get(`${API_BASE}/vehicle-types`, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Failed to load vehicle types");
        return;
      }

      const types = Array.isArray(r.data?.data) ? r.data.data : [];
      const normalizedTypes = types.map((x) => ({
        id:
          x?.id ??
          x?.vehicleTypeId ??
          x?.vehicletypeid ??
          x?.fld_vehicletypeid ??
          x?.fld_vehicleTypeId ??
          0,
        name:
          x?.name ??
          x?.vehicleType ??
          x?.vehicletypename ??
          x?.fld_vehicletypename ??
          x?.["Vehicle Type"] ??
          "",
      }));

      setVehicleTypes(normalizedTypes);

      if (!urlVehicleTypeId && !vehicleTypeId && normalizedTypes.length) {
        setVehicleTypeId(String(normalizedTypes[0].id));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vehicle types");
    }
  };

  const fetchSubTypes = async (vehTypeIdStr) => {
    const id = toInt(vehTypeIdStr, 0);
    if (!id) {
      setSubTypes([]);
      return;
    }

    setListLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/list`, {
        params: { vehicleTypeId: id },
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Failed to load sub types");
        setSubTypes([]);
        return;
      }

      const rows = (r.data?.data || []).map((x) => ({
        id:
          x?.id ??
          x?.subTypeId ??
          x?.vehicleSubTypeId ??
          x?.vehiclesubtypeid ??
          x?.fld_vehiclesubtypeid ??
          0,
        vehicleTypeId: String(id),
        vehicleSubType:
          x?.vehicleSubType ??
          x?.vehicle_subtype ??
          x?.fld_vehicle_subtypename ??
          x?.vehiclesubtypename ??
          "",
      }));

      setSubTypes(rows);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load sub types");
      setSubTypes([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicleTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  useEffect(() => {
    if (!vehicleTypeId) return;

    if (urlVehicleTypeId && toInt(vehicleTypeId) !== urlVehicleTypeId) {
      setVehicleTypeId(String(urlVehicleTypeId));
      return;
    }

    fetchSubTypes(vehicleTypeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleTypeId, urlVehicleTypeId, commonHeaders]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => inputRef.current?.focus(), 250);
  };

  const handleSave = async () => {
    if (!toStr(vehicleTypeId) || !toStr(formData.vehicleSubType)) {
      toast.error("Please fill in all fields!");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        subTypeId: editingId ? toInt(editingId) : 0,
        vehicleTypeId: toInt(vehicleTypeId),
        vehicleSubType: toStr(formData.vehicleSubType),
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Failed to save vehicle sub type!");
        return;
      }

      toast.success(
        r.data?.message || (editingId ? "Updated Successfully" : "Saved Successfully")
      );

      setEditingId(null);
      setFormData({ vehicleSubType: "" });

      await fetchSubTypes(vehicleTypeId);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save vehicle sub type!");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ vehicleSubType: item.vehicleSubType });
    scrollToForm();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sub type?")) return;

    try {
      const r = await axios.post(
        `${API_BASE}/delete`,
        { ids: [toInt(id)] },
        { headers: commonHeaders }
      );

      if (!r.data?.success) {
        toast.error(r.data?.message || "Delete failed");
        return;
      }

      toast.success(r.data?.message || "Deleted Successfully");

      if (editingId === id) {
        setEditingId(null);
        setFormData({ vehicleSubType: "" });
      }

      await fetchSubTypes(vehicleTypeId);
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setFormData({ vehicleSubType: "" });
  };

  const startAddNew = () => {
    handleCancelEdit();
    scrollToForm();
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SUB TYPE</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* ==================== HEADER ==================== */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Sub Types
              </h2>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {subTypes.length} Records
              </span>
              {editingId ? (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-amber-900 text-amber-100" : "bg-amber-100 text-amber-800"
                  }`}
                >
                  Editing ID: {editingId}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
             
              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back to Listing
              </button>
            </div>
          </div>

          {/* ==================== ADD / EDIT FORM ==================== */}
          <div
            ref={formRef}
            className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}
          >
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>
                {editingId ? "Edit Vehicle Sub Type" : "Add Vehicle Sub Type"}
              </h3>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={editingId ? handleCancelEdit : () => setFormData({ vehicleSubType: "" })}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold border ${
                    isDark
                      ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-white"
                  }`}
                >
                  {editingId ? "Cancel" : "Clear"}
                </button>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || !toStr(vehicleTypeId) || !toStr(formData.vehicleSubType)}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    loading || !toStr(vehicleTypeId) || !toStr(formData.vehicleSubType)
                      ? "opacity-60 cursor-not-allowed bg-gray-400"
                      : `${buttonClass} ${hoverClass}`
                  } text-white`}
                >
                  <FaSave />
                  {loading
                    ? editingId
                      ? "Updating..."
                      : "Saving..."
                    : editingId
                    ? "Update"
                    : "Save"}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className={labelClass}>Vehicle Type</label>
                <select
                  value={String(vehicleTypeId || "")}
                  disabled
                  className={`${fieldClass} opacity-80 cursor-not-allowed`}
                >
                  <option value={String(vehicleTypeId || "")}>
                    {selectedVehicleTypeName || (vehicleTypeId ? "Loading..." : "No Vehicle Type")}
                  </option>
                </select>
              </div>

              <div>
                <label htmlFor="vehicleSubType" className={labelClass}>
                  Vehicle Sub Type
                </label>
                <input
                  ref={inputRef}
                  id="vehicleSubType"
                  name="vehicleSubType"
                  type="text"
                  value={formData.vehicleSubType}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSave();
                  }}
                  placeholder="Enter vehicle sub type..."
                  className={fieldClass}
                />
              </div>
            </div>
          </div>

          {/* ==================== RESULTS TABLE ==================== */}
          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </div>
              </div>
            ) : subTypes.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No vehicle sub types found.
                </div>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">S.No</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Vehicle Sub Type</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Edit</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Delete</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {subTypes.map((item, index) => {
                    const isEditingRow = editingId && item.id === editingId;

                    return (
                      <tr
                        key={item.id}
                        className={`${
                          isEditingRow
                            ? isDark
                              ? "bg-amber-900/30"
                              : "bg-amber-50"
                            : index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-white"
                            : isDark
                            ? "bg-gray-750"
                            : "bg-gray-50"
                        } transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                      >
                        <td className={`px-6 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {index + 1}
                        </td>

                        <td className={`px-6 py-4 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>
                          {selectedVehicleTypeName}
                        </td>

                        <td className={`px-6 py-4 text-sm ${isDark ? "text-white" : "text-gray-900"}`}>
                          {item.vehicleSubType}
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            className="text-yellow-500 hover:text-yellow-600 text-lg transition"
                            title="Edit"
                          >
                            ✏️
                          </button>
                        </td>

                        <td className="px-6 py-4 text-center">
                          <button
                            type="button"
                            onClick={() => handleDelete(item.id)}
                            className="text-red-500 hover:text-red-600 transition"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSubTypeMaster;